import React from "react";
import { ChipBadge, EmptyBadge } from "./ChipsUsed";
import { ChipsUsedRow, ChipName } from "../../types";

export const ChipsUsedList: React.FC<{
  rows: ChipsUsedRow[];
  expectedChips: ChipName[];
  currentGameweek: number | null;
}> = ({ rows, expectedChips, currentGameweek }) => (
  <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
    {rows.map((row) => (
      <ChipsUsedCard
        key={row.teamId}
        row={row}
        expectedChips={expectedChips}
        currentGameweek={currentGameweek}
      />
    ))}
  </ul>
);

export const ChipsUsedCard: React.FC<{
  row: ChipsUsedRow;
  expectedChips: ChipName[];
  currentGameweek: number | null;
}> = ({ row, expectedChips, currentGameweek }) => {
  const firstHalfSeasonChipsUsed = row.chips.filter(
    (c) => c.event && c.event <= 19,
  );
  const secondHalfSeasonChipsUsed = row.chips.filter(
    (c) => c.event && c.event > 19,
  );
  const chipsToTrack =
    currentGameweek && currentGameweek > 19
      ? secondHalfSeasonChipsUsed
      : firstHalfSeasonChipsUsed;
  const usedByName = chipsToTrack.reduce<Record<string, any[]>>((acc, c) => {
    const key = c.name?.toLowerCase() || "unknown";
    (acc[key] ||= []).push(c);
    return acc;
  }, {});
  const chronological = [...row.chips].sort(
    (a, b) => (a.event || 0) - (b.event || 0),
  );

  return (
    <li className="rounded-2xl bg-surface ring-1 ring-line p-3">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <h3 className="text-base font-semibold truncate">
            {row.managerName}
          </h3>
        </div>
        <div className="text-right text-xs text-content-muted">
          <span className="inline-block rounded-lg bg-surface-sunken px-2 py-1 ring-1 ring-line">
            {row.chips.length} used
          </span>
        </div>
      </div>
      <div className="space-y-2">
        {chronological.length > 0 ? (
          chronological.map((c, idx) => (
            <ChipBadge key={idx} name={c.name} event={c.event} time={c.time} />
          ))
        ) : (
          <div className="text-sm text-content-muted">No chips used yet.</div>
        )}
      </div>
      <div className="mt-3">
        <p className="text-2xs uppercase tracking-wide text-content-muted mb-1">
          Unused
        </p>
        <div className="flex flex-wrap gap-2">
          {expectedChips
            .filter((c) => !usedByName[c])
            .map((missing) => (
              <EmptyBadge key={`${row.teamId}-${missing}`} expected={missing} />
            ))}
        </div>
      </div>
    </li>
  );
};
